'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './BlogImageViewer.module.scss';

export function BlogImageViewer({
  children,
  closeLabel = '닫기',
  label = '이미지 확대 보기',
}: {
  children: React.ReactNode;
  closeLabel?: string;
  // dialog 접근성 이름 (aria-label)
  label?: string;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const overlayRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const transformRef = useRef(transform);
  transformRef.current = transform;
  const dragOrigin = useRef({ mouseX: 0, mouseY: 0, transX: 0, transY: 0 });

  const clamp = useCallback((x: number, y: number, scale: number) => {
    const el = imgRef.current;
    if (!el) return { x, y };
    const MARGIN = 120;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const hw = (el.offsetWidth * scale) / 2;
    const hh = (el.offsetHeight * scale) / 2;
    return {
      x: Math.min(vw / 2 - MARGIN + hw, Math.max(-(vw / 2 - MARGIN + hw), x)),
      y: Math.min(vh / 2 - MARGIN + hh, Math.max(-(vh / 2 - MARGIN + hh), y)),
    };
  }, []);

  const close = useCallback(() => {
    setSrc(null);
    setTransform({ scale: 1, x: 0, y: 0 });
  }, []);

  const handleContainerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const t = e.target as HTMLElement;
      if (t.tagName === 'IMG') {
        setSrc((t as HTMLImageElement).src);
        setTransform({ scale: 1, x: 0, y: 0 });
      }
    },
    [],
  );

  useEffect(() => {
    const el = overlayRef.current;
    if (!el || !src) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const { scale, x, y } = transformRef.current;
      const next = Math.min(2, Math.max(0.5, scale - e.deltaY * 0.001));
      const cx = e.clientX - window.innerWidth / 2;
      const cy = e.clientY - window.innerHeight / 2;
      const nx = cx - (cx - x) * (next / scale);
      const ny = cy - (cy - y) * (next / scale);
      const clamped = clamp(nx, ny, next);
      setTransform({ scale: next, ...clamped });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [src, clamp]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const { x, y } = transformRef.current;
    dragOrigin.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      transX: x,
      transY: y,
    };
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const { mouseX, mouseY, transX, transY } = dragOrigin.current;
      const { scale } = transformRef.current;
      const nx = transX + (e.clientX - mouseX);
      const ny = transY + (e.clientY - mouseY);
      const clamped = clamp(nx, ny, scale);
      setTransform((prev) => ({ ...prev, ...clamped }));
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, clamp]);

  useEffect(() => {
    if (!src) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [src, close]);

  useEffect(() => {
    document.body.style.overflow = src ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [src]);

  // 열릴 때 닫기 버튼으로 포커스 이동, 닫힐 때 원래 위치로 복원.
  useEffect(() => {
    if (!src) return;
    const previouslyFocused = document.activeElement;
    closeButtonRef.current?.focus();
    return () => {
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, [src]);

  const pct = Math.round(transform.scale * 100);

  return (
    <>
      <div onClick={handleContainerClick} className={styles.container}>
        {children}
      </div>

      {src ? (
        <div
          ref={overlayRef}
          role="dialog"
          aria-modal="true"
          aria-label={label}
          className={styles.overlay}
          onClick={close}
          style={{ cursor: isDragging ? 'grabbing' : 'default' }}
        >
          <button
            ref={closeButtonRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            className={styles.closeButton}
            aria-label={closeLabel}
          >
            ✕
          </button>

          <div className={styles.zoomIndicator}>{pct}%</div>

          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            ref={imgRef}
            src={src}
            alt=""
            draggable={false}
            onMouseDown={onMouseDown}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
              transformOrigin: 'center',
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none',
              transition: isDragging ? 'none' : 'transform 0.05s ease-out',
            }}
            className={styles.viewerImage}
          />
        </div>
      ) : null}
    </>
  );
}
